import { noop } from 'lodash';
import { createContext } from 'react';

import { useGuardedContext } from '@/hooks/useGuardedContext';
import { AiSuggestion_DispositiveProvisionFragment } from '@/modules/aiDP/graphql/aiDP.generated';
import { MatchableKind } from '@/modules/aiSuggestions/aiSuggestions.types';
import { AiSuggestionKind } from '@/types/schema';
import { PulidKind } from '@/utils/pulid';

export interface DispositiveProvisionSuggestionsContext {
  loading: boolean;

  targetId: string;
  setTargetId: (targetId: string) => void;

  /** The kind of thing that the DP suggestions are for. */
  targetKind: PulidKind | null;

  /** DP suggestions grouped by kind. Includes both acknowledged and unacknowledged suggestions. */
  suggestionsByKind: Partial<
    Record<AiSuggestionKind, AiSuggestion_DispositiveProvisionFragment[]>
  >;

  /**
   * Returns the suggestion of a specific kind that got matched with the given id.
   */
  getMatchableSuggestion: (
    kind: MatchableKind,
    matchedId: string
  ) => AiSuggestion_DispositiveProvisionFragment | undefined;

  /** Refetch the suggestions for the given entity or TE id. */
  refetchSuggestions: (targetId: string) => void;

  /** The document ids that were the source of the suggestions. */
  suggestionDocumentIds: string[];
}

export const DispositiveProvisionSuggestionsContext =
  createContext<DispositiveProvisionSuggestionsContext>({
    loading: true,
    targetId: '',
    setTargetId: noop,
    targetKind: null,
    suggestionsByKind: {},
    getMatchableSuggestion: () => undefined,
    refetchSuggestions: noop,
    suggestionDocumentIds: [],
  });

export const useDispositiveProvisionSuggestionsContext = () => {
  return useGuardedContext(
    DispositiveProvisionSuggestionsContext,
    'DispositiveProvisionSuggestionsContextProvider'
  );
};
